import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Skeleton } from "primereact/skeleton";
import { Tooltip } from "primereact/tooltip";
import C from "classnames";

import { Badge } from "sj-ui-toolkit/src/atlas/components/Badge";
import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import type {
  DashboardSnapshot,
  LeaderBoardSnapshot,
} from "sj-ui-toolkit/src/api/dashboardSnapshot";
import type { PassedBelt } from "sj-ui-toolkit/src/api/hooks";

import S from "./UserSnapshot.module.scss";

interface UserSnapshotProps {
  // Dashboard passes `{}` while the snapshot is loading
  userSnapshot: Partial<DashboardSnapshot>;
  leaderboard: LeaderBoardSnapshot["users"];
  achievements?: PassedBelt[];
  isLoading?: boolean;
  showLeaderboard?: boolean;
}

export const UserSnapshot = ({
  userSnapshot,
  leaderboard,
  achievements = [],
  isLoading = false,
  showLeaderboard = true,
}: UserSnapshotProps) => {
  const { navigateTo } = useSmartNavigation();

  if (isLoading) {
    return (
      <div className={S.userSnapshot}>
        <div className={S.section}>
          <Skeleton width="80px" height="1.5rem" className="mb-2" />
          <Skeleton width="100%" height="101px" />
        </div>
        <div className={S.section}>
          <Skeleton width="150px" height="1.5rem" className="mb-2" />
          <Skeleton width="100%" height="101px" />
        </div>
        <div className={S.section}>
          <Skeleton width="120px" height="1.5rem" className="mb-2" />
          <Skeleton width="100%" height="101px" />
        </div>
      </div>
    );
  }

  if (Object.keys(userSnapshot).length === 0 && !leaderboard?.length) {
    return null;
  }

  const { currentLevel } = userSnapshot;
  const recentAchievements = achievements.slice(0, 3);

  const allLeaderboardPointsZero =
    leaderboard?.length > 0 && leaderboard.every(entry => entry.points === 0);

  return (
    <div className={S.userSnapshot}>
      {currentLevel && (
        <div className={S.section}>
          <h2 className="atlas-h5 mb-2">Level</h2>
          <div className={S.content}>
            <p className="body-md">{currentLevel.levelName}</p>
            <ProgressBar
              value={(currentLevel.statsProgress ?? 0) * 100}
              showValue={false}
              className={S.levelProgress}
              aria-label={`Level progress: ${currentLevel.levelName}`}
            />
            <p>
              {currentLevel.statsCurrent}
              <span className={S.mutedText}>
                {" / "}
                {currentLevel.statsTotal} {(currentLevel.id ?? 0) > 3 ? "points" : "lessons"} to
                next level
              </span>
            </p>
          </div>
        </div>
      )}

      {recentAchievements.length > 0 && (
        <div className={C(S.section, S.achievementsSection)}>
          <h2 className="atlas-h5 mb-2">Certificates</h2>
          <div className={S.content}>
            <div className={S.achievementsBadges}>
              {recentAchievements.map(belt => (
                <div
                  key={belt.id}
                  className={`${S.achievementBadge} achievement-badge-tooltip`}
                  data-pr-tooltip={belt.name}
                >
                  <Badge name="path" complete size={56} />
                </div>
              ))}
              <Tooltip target=".achievement-badge-tooltip" position="top" />
            </div>
            <Button
              label="View all certificates"
              severity="secondary"
              text
              icon="pi pi-arrow-right"
              iconPos="right"
              className={S.achievementsLink}
              onClick={() => navigateTo(buildRoute("User.routes.certificates.route"))}
            />
          </div>
        </div>
      )}

      {showLeaderboard && leaderboard?.length > 0 && (
        <div className={S.section}>
          <h2 className="atlas-h5">Leaderboard</h2>
          <div className={S.content}>
            {allLeaderboardPointsZero ? (
              <div className="mt-3">
                Take a lesson from the full catalog or enroll in a path to earn points and get on
                the leaderboard!
              </div>
            ) : (
              <div className={S.leaderboard}>
                {leaderboard.map(entry => (
                  <div
                    key={entry.userId}
                    className={C(S.leaderboardEntry, entry.isSelf && S.isSelf)}
                  >
                    <div className={S.leaderboardPosition}>{entry.position}</div>
                    <div className={S.leaderboardName}>{entry.fullName}</div>
                    <div className={C(S.leaderboardPoints, "uppercase")}>{entry.points} points</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
